import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./components/ui/dialog";
import { Loader2, RotateCcw, Save, Calendar, Plus, X } from "lucide-react";

/**
 * AdminSettingsDialog — small admin-only screen to edit global settings
 * that are otherwise hard-coded (currently: the booking disclaimer shown
 * on the custom-rows booking dialog). More settings can be added over
 * time without another modal.
 */
const DEFAULT_DISCLAIMER =
  "You are responsible for choosing the correct ticket type for each attendee based on age or category. ACE APS declines all responsibility for booking mistakes; refunds or corrections are at the sole discretion of the administration.";

const AdminSettingsDialog = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [text, setText] = useState("");
  const [initialText, setInitialText] = useState("");

  // Cross-year signup: window opening (MM-DD) + per-year membership fees.
  const [renewalMd, setRenewalMd] = useState("09-01");
  const [renewalMdInitial, setRenewalMdInitial] = useState("09-01");
  const [fees, setFees] = useState([]); // [{year:"2026", fee:"35.00"}]
  const [feesInitial, setFeesInitial] = useState([]);
  const [savingRenewal, setSavingRenewal] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    Promise.all([
      axios.get("/settings/booking-disclaimer").then((res) => res.data).catch(() => null),
      axios.get("/settings/renewal-config").then((res) => res.data).catch(() => null),
    ])
      .then(([discl, renewal]) => {
        const t = discl?.text || DEFAULT_DISCLAIMER;
        setText(t);
        setInitialText(t);
        const md = renewal?.renewal_window_open_md || "09-01";
        setRenewalMd(md);
        setRenewalMdInitial(md);
        const feeMap = renewal?.membership_fee_per_year || {};
        const feeRows = Object.entries(feeMap)
          .sort((a, b) => Number(a[0]) - Number(b[0]))
          .map(([year, fee]) => ({ year, fee: String(fee) }));
        setFees(feeRows);
        setFeesInitial(feeRows);
      })
      .finally(() => setLoading(false));
  }, [isOpen]);

  const save = async () => {
    const clean = (text || "").trim();
    if (!clean) {
      toast.error("Disclaimer text cannot be empty.");
      return;
    }
    setSaving(true);
    try {
      await axios.put("/settings/booking-disclaimer", { text: clean });
      setInitialText(clean);
      toast.success("Disclaimer updated. Members will see the new text on their next booking.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the disclaimer.");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => setText(DEFAULT_DISCLAIMER);

  const isDirty = text !== initialText;

  // ── Cross-year signup helpers ─────────────────────────────────────────
  const addFeeRow = () => {
    // Default the next year suggestion to (highest existing year + 1) or
    // the current calendar year.
    const nextY = fees.length
      ? Math.max(...fees.map((f) => Number(f.year))) + 1
      : new Date().getFullYear();
    setFees([...fees, { year: String(nextY), fee: "" }]);
  };
  const removeFeeRow = (i) => setFees(fees.filter((_, idx) => idx !== i));
  const updateFee = (i, patch) =>
    setFees(fees.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const _MD_RE = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
  const renewalConfigDirty =
    renewalMd !== renewalMdInitial ||
    JSON.stringify(fees) !== JSON.stringify(feesInitial);

  const saveRenewalConfig = async () => {
    if (!_MD_RE.test(renewalMd)) {
      toast.error("Opening date must be in MM-DD format (e.g., 09-01).");
      return;
    }
    // Build the fees map and validate.
    const map = {};
    for (const row of fees) {
      const y = parseInt(row.year, 10);
      if (!y || y < 2020 || y > 2100) {
        toast.error(`Invalid year: ${row.year}`);
        return;
      }
      if (row.fee === "" || row.fee === null || row.fee === undefined) continue;
      const amount = Number(row.fee);
      if (!Number.isFinite(amount) || amount < 0 || amount > 10000) {
        toast.error(`Invalid fee for ${row.year}`);
        return;
      }
      map[String(y)] = Math.round(amount * 100) / 100;
    }
    setSavingRenewal(true);
    try {
      const res = await axios.put("/settings/renewal-config", {
        renewal_window_open_md: renewalMd,
        membership_fee_per_year: map,
      });
      setRenewalMdInitial(res.data?.renewal_window_open_md || renewalMd);
      const nextFees = Object.entries(res.data?.membership_fee_per_year || {})
        .sort((a, b) => Number(a[0]) - Number(b[0]))
        .map(([year, fee]) => ({ year, fee: String(fee) }));
      setFees(nextFees);
      setFeesInitial(nextFees);
      toast.success("Membership config saved.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save membership config.");
    } finally {
      setSavingRenewal(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="admin-settings-dialog">
        <DialogHeader>
          <DialogTitle>Global settings</DialogTitle>
          <DialogDescription>
            Edit shared strings used across the platform. Changes are applied instantly.
          </DialogDescription>
        </DialogHeader>

        <section className="space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-slate-900">Booking disclaimer (custom-rows events)</h4>
            <p className="text-xs text-slate-500">
              Displayed above the total on the member booking dialog. Members must tick a box confirming they've
              read it before paying.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 py-6 text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              className="text-sm"
              data-testid="disclaimer-textarea"
            />
          )}

          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{text.length} characters</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={resetToDefault}
              className="h-7 text-slate-600"
              data-testid="disclaimer-reset-default"
            >
              <RotateCcw className="h-3 w-3 mr-1" /> Reset to default
            </Button>
          </div>
        </section>

        <section className="space-y-3 pt-4 border-t border-slate-200">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-emerald-700" />
              Membership year & renewal window
            </h4>
            <p className="text-xs text-slate-500">
              Choose the annual date renewal opens and set the membership fee for each upcoming year.
              A year with no fee configured is hidden from new signups.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <div>
              <label className="text-xs text-slate-600 mb-1 block" htmlFor="renewal-md">
                Renewal window opens each year on (MM-DD)
              </label>
              <Input
                id="renewal-md"
                value={renewalMd}
                onChange={(e) => setRenewalMd(e.target.value)}
                placeholder="09-01"
                className="h-9 max-w-[140px] font-mono"
                data-testid="renewal-md-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                From this date until 31 Dec, new signups also see next year's option.
              </p>
            </div>
          </div>

          <div className="mt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-700">Membership fee per year (€)</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={addFeeRow}
                data-testid="add-fee-row"
              >
                <Plus className="h-3 w-3 mr-1" /> Add year
              </Button>
            </div>
            {fees.length === 0 ? (
              <p className="text-xs text-slate-500 italic">
                No fees configured yet. Add at least one year so members can sign up.
              </p>
            ) : (
              <div className="space-y-1.5">
                {fees.map((row, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`fee-row-${i}`}>
                    <Input
                      value={row.year}
                      onChange={(e) => updateFee(i, { year: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) })}
                      placeholder="Year"
                      className="h-8 w-20 font-mono"
                      data-testid={`fee-year-${i}`}
                    />
                    <span className="text-slate-400">€</span>
                    <Input
                      value={row.fee}
                      onChange={(e) => updateFee(i, { fee: e.target.value.replace(/[^0-9.]/g, "") })}
                      placeholder="e.g. 35.00"
                      className="h-8 w-28 font-mono"
                      data-testid={`fee-amount-${i}`}
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-red-600 hover:bg-red-50"
                      onClick={() => removeFeeRow(i)}
                      aria-label="Remove"
                      data-testid={`fee-remove-${i}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end pt-1">
            <Button
              onClick={saveRenewalConfig}
              disabled={savingRenewal || loading || !renewalConfigDirty}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
              data-testid="renewal-save"
            >
              {savingRenewal ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Saving…</>
              ) : (
                <><Save className="h-3.5 w-3.5 mr-1" /> Save membership config</>
              )}
            </Button>
          </div>
        </section>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Close</Button>
          <Button
            onClick={save}
            disabled={saving || loading || !isDirty}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="disclaimer-save"
          >
            {saving ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</>) : (<><Save className="h-4 w-4 mr-1" /> Save</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminSettingsDialog;
